import { useState, useEffect } from 'react';
import { getKostenstellen, createKostenstelle, updateKostenstelle, deleteKostenstelle } from '../services/api';
import { Folders, Plus, Trash, PencilSimple, X, Check, Warning } from '@phosphor-icons/react';

const VERTEILERSCHLUESSEL = [
  { value: 'flaeche', label: 'Nach Fläche (m²)' },
  { value: 'personen', label: 'Nach Personenzahl' },
  { value: 'einheiten', label: 'Nach Einheiten' },
  { value: 'verbrauch', label: 'Nach Verbrauch' },
];

export default function Kostenstellen() {
  const [kostenstellen, setKostenstellen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingKs, setEditingKs] = useState(null);
  const [filter, setFilter] = useState('alle'); // alle, umlagefaehig, nicht_umlagefaehig

  const loadData = async () => {
    try {
      const res = await getKostenstellen();
      setKostenstellen(res.data);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diese Kostenstelle wirklich löschen?')) return;
    try {
      await deleteKostenstelle(id);
      loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const filteredKostenstellen = kostenstellen.filter(ks => {
    if (filter === 'umlagefaehig') return ks.umlagefaehig === true;
    if (filter === 'nicht_umlagefaehig') return ks.umlagefaehig === false;
    return true;
  });

  const umlagefaehigCount = kostenstellen.filter(k => k.umlagefaehig).length;
  const nichtUmlagefaehigCount = kostenstellen.filter(k => !k.umlagefaehig).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="kostenstellen-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">Kostenstellen</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Kategorien nach BetrKV für die Nebenkostenabrechnung</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-slate-900 text-white px-4 sm:px-6 py-2.5 font-medium hover:bg-slate-800 flex items-center justify-center gap-2 w-full sm:w-auto"
          data-testid="add-kostenstelle-button"
        >
          <Plus weight="bold" className="w-4 h-4" />
          Kostenstelle hinzufügen
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setFilter('alle')}
          className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${filter === 'alle' ? 'bg-slate-900 text-white' : 'bg-white border border-gray-200 text-slate-600 hover:bg-gray-50'}`}
        >
          Alle ({kostenstellen.length})
        </button>
        <button
          onClick={() => setFilter('umlagefaehig')}
          className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${filter === 'umlagefaehig' ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-200 text-slate-600 hover:bg-gray-50'}`}
        >
          Umlagefähig ({umlagefaehigCount})
        </button>
        <button
          onClick={() => setFilter('nicht_umlagefaehig')}
          className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${filter === 'nicht_umlagefaehig' ? 'bg-amber-600 text-white' : 'bg-white border border-gray-200 text-slate-600 hover:bg-gray-50'}`}
        >
          Nicht umlagefähig ({nichtUmlagefaehigCount})
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
        <p className="font-medium mb-1">Hinweis zur Betriebskostenverordnung (BetrKV)</p>
        <p>Umlagefähige Kosten nach §2 BetrKV können auf Mieter umgelegt werden. Nicht umlagefähige Kosten (z.B. Reparaturen, Verwaltung) trägt der Vermieter.</p>
      </div>

      {/* Kostenstellen Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {filteredKostenstellen.map((ks) => (
          <div 
            key={ks.id} 
            className={`bg-white border p-4 ${ks.umlagefaehig ? 'border-emerald-200' : 'border-amber-200'}`}
            data-testid={`kostenstelle-card-${ks.id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 flex items-center justify-center ${ks.umlagefaehig ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                  {ks.umlagefaehig ? (
                    <Check weight="bold" className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Warning weight="bold" className="w-4 h-4 text-amber-600" />
                  )}
                </div>
                {ks.betrk_nummer && (
                  <span className="text-xs font-mono bg-slate-100 px-2 py-0.5 text-slate-600">
                    §2 Nr. {ks.betrk_nummer}
                  </span>
                )}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => { setEditingKs(ks); setShowModal(true); }}
                  className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                  data-testid={`edit-ks-${ks.id}`}
                >
                  <PencilSimple weight="bold" className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(ks.id)}
                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50"
                  data-testid={`delete-ks-${ks.id}`}
                >
                  <Trash weight="bold" className="w-4 h-4" />
                </button>
              </div>
            </div>
            <h3 className="font-medium text-slate-900 mb-1">{ks.name}</h3>
            {ks.beschreibung && (
              <p className="text-xs text-slate-500 mb-2">{ks.beschreibung}</p>
            )}
            <div className="flex items-center justify-between text-xs">
              <span className={`px-2 py-0.5 font-medium ${ks.umlagefaehig ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                {ks.umlagefaehig ? 'Umlagefähig' : 'Nicht umlagefähig'}
              </span>
              <span className="text-slate-500">
                {VERTEILERSCHLUESSEL.find(v => v.value === ks.verteilerschluessel)?.label || ks.verteilerschluessel}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      <KostenstelleModal
        open={showModal}
        onClose={() => { setShowModal(false); setEditingKs(null); }}
        kostenstelle={editingKs}
        onSave={() => { loadData(); setShowModal(false); setEditingKs(null); }}
      />
    </div>
  );
}

function KostenstelleModal({ open, onClose, kostenstelle, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    beschreibung: '',
    umlagefaehig: true,
    verteilerschluessel: 'flaeche',
    betrk_nummer: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (kostenstelle) {
      setFormData({
        name: kostenstelle.name || '',
        beschreibung: kostenstelle.beschreibung || '',
        umlagefaehig: kostenstelle.umlagefaehig ?? true,
        verteilerschluessel: kostenstelle.verteilerschluessel || 'flaeche',
        betrk_nummer: kostenstelle.betrk_nummer || ''
      });
    } else {
      setFormData({ name: '', beschreibung: '', umlagefaehig: true, verteilerschluessel: 'flaeche', betrk_nummer: '' });
    }
  }, [kostenstelle]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (kostenstelle) {
        await updateKostenstelle(kostenstelle.id, formData);
      } else {
        await createKostenstelle(formData);
      }
      onSave();
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="kostenstelle-modal">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="font-heading text-lg sm:text-xl font-bold text-slate-900">
            {kostenstelle ? 'Kostenstelle bearbeiten' : 'Neue Kostenstelle'}
          </h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600">
            <X weight="bold" className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
              placeholder="z.B. Gartenpflege"
              required
              data-testid="ks-name-input"
            />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Beschreibung (optional)</label>
            <input
              type="text"
              value={formData.beschreibung}
              onChange={(e) => setFormData(prev => ({ ...prev, beschreibung: e.target.value }))}
              className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
              placeholder="Kurze Beschreibung"
              data-testid="ks-beschreibung-input"
            />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">BetrKV Nummer (optional)</label>
            <input
              type="text"
              value={formData.betrk_nummer}
              onChange={(e) => setFormData(prev => ({ ...prev, betrk_nummer: e.target.value }))}
              className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
              placeholder="z.B. 10 (für §2 Nr. 10)"
              data-testid="ks-betrk-input"
            />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Verteilerschlüssel</label>
            <select
              value={formData.verteilerschluessel}
              onChange={(e) => setFormData(prev => ({ ...prev, verteilerschluessel: e.target.value }))}
              className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
              data-testid="ks-verteiler-select"
            >
              {VERTEILERSCHLUESSEL.map(v => (
                <option key={v.value} value={v.value}>{v.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-3">Umlagefähigkeit</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, umlagefaehig: true }))}
                className={`flex-1 py-3 font-medium flex items-center justify-center gap-2 ${
                  formData.umlagefaehig 
                    ? 'bg-emerald-100 text-emerald-800 border-2 border-emerald-500' 
                    : 'bg-gray-100 text-slate-600 border-2 border-transparent'
                }`}
                data-testid="ks-umlagefaehig-yes"
              >
                <Check weight="bold" className="w-4 h-4" />
                Umlagefähig
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, umlagefaehig: false }))}
                className={`flex-1 py-3 font-medium flex items-center justify-center gap-2 ${
                  !formData.umlagefaehig 
                    ? 'bg-amber-100 text-amber-800 border-2 border-amber-500' 
                    : 'bg-gray-100 text-slate-600 border-2 border-transparent'
                }`}
                data-testid="ks-umlagefaehig-no"
              >
                <Warning weight="bold" className="w-4 h-4" />
                Nicht umlagefähig
              </button>
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-200 text-slate-900 py-2.5 font-medium hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-slate-900 text-white py-2.5 font-medium hover:bg-slate-800 disabled:opacity-50"
              data-testid="ks-save-button"
            >
              {loading ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
